import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { VideoStream } from './VideoStream';
import { PoseProcessor } from './PoseProcessor';
import { PoseData } from '../../types';

/**
 * PoseDetector class manages MediaPipe Pose detection lifecycle
 * Validates Requirements 3.1.2, 3.1.4
 */
export class PoseDetector {
  private poseLandmarker: PoseLandmarker | null = null;
  private videoStream: VideoStream;
  private poseProcessor: PoseProcessor;
  private isRunning: boolean = false;
  private animationFrameId: number | null = null;
  private lastDetectionTime: number = 0;
  private lastValidPosesData: PoseData[] = [];
  private posesCallbacks: Array<(poses: PoseData[]) => void> = [];
  
  // Target 30fps = ~33ms per frame
  private readonly TARGET_FRAME_TIME_MS = 33;

  constructor(videoStream: VideoStream) {
    this.videoStream = videoStream;
    this.poseProcessor = new PoseProcessor();
  }

  /**
   * Initialize MediaPipe Pose model
   * @param config Optional configuration with numPoses parameter (defaults to 2 for multi-person MVP)
   */
  async initialize(config?: { numPoses?: number }): Promise<void> {
    const numPoses = config?.numPoses ?? 2; // Default to 2 for MVP (configurable)
    
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numPoses: numPoses,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
    } catch (error) {
      throw new Error(`Failed to initialize MediaPipe Pose: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Start pose detection loop
   */
  start(): void {
    if (!this.poseLandmarker) {
      throw new Error('PoseDetector not initialized. Call initialize() first.');
    }

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.lastDetectionTime = performance.now();
    this.detectPose();
  }

  /**
   * Stop pose detection loop
   */
  stop(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Get all currently detected poses (multi-person mode)
   * @returns Array of detected poses (empty array if no poses detected)
   */
  getCurrentPoses(): PoseData[] {
    return this.lastValidPosesData;
  }


  /**
   * Subscribe to multi-person pose updates
   * @param callback Function to call when poses are detected
   * @returns Unsubscribe function
   */
  onPosesUpdate(callback: (poses: PoseData[]) => void): () => void {
    this.posesCallbacks.push(callback);
    return () => {
      const index = this.posesCallbacks.indexOf(callback);
      if (index > -1) {
        this.posesCallbacks.splice(index, 1);
      }
    };
  }


  /**
   * Main detection loop - runs at 30fps target (throttled)
   */
  private detectPose(): void {
    if (!this.isRunning || !this.poseLandmarker) {
      return;
    }

    const videoElement = this.videoStream.getVideoElement();
    const now = performance.now();
    const timeSinceLastDetection = now - this.lastDetectionTime;

    // Throttle to 30fps - only run detection if enough time has passed
    if (timeSinceLastDetection >= this.TARGET_FRAME_TIME_MS) {
      const detectionStartTime = performance.now();
      
      try {
        // Run detection on current video frame
        const result = this.poseLandmarker.detectForVideo(videoElement, now);
        
        const detectionEndTime = performance.now();
        const detectionTime = detectionEndTime - detectionStartTime;
        
        // Log warning if detection takes too long (>50ms = <20fps)
        if (detectionTime > 50) {
          console.warn(`⚠️ Pose detection slow: ${detectionTime.toFixed(1)}ms (target: <50ms)`);
        }
        
        if (result && result.landmarks && result.landmarks.length > 0) {
          // Valid pose(s) detected
          // Convert MediaPipe result to array of PoseData
          const posesData = this.poseProcessor.process(result);
          this.lastValidPosesData = posesData;
          
          this.lastDetectionTime = now;
          this.notifySubscribers();
        } else {
          // No pose detected, but continue with last known pose if recent
          this.handleNoDetection(now);
        }
      } catch (error) {
        // Detection failed, continue with last known pose
        console.warn('Pose detection failed:', error);
        this.handleNoDetection(now);
      }
    }

    // Schedule next detection check using requestAnimationFrame
    this.animationFrameId = requestAnimationFrame(() => this.detectPose());
  }

  /**
   * Handle case where no pose is detected
   * Mark pose as not detected after 500ms of failed detection
   */
  private handleNoDetection(now: number): void {
    const timeSinceLastDetection = now - this.lastDetectionTime;
    
    if (timeSinceLastDetection > 500) {
      // More than 500ms without detection, mark as not detected
      this.lastValidPosesData = [];
    }
    // Within the grace period we keep the last valid poses as-is.
    this.notifySubscribers();
  }

  /**
   * Notify all subscribers of pose update
   */
  private notifySubscribers(): void {
    // Notify multi-person pose callbacks
    this.posesCallbacks.forEach(callback => {
      try {
        callback(this.lastValidPosesData);
      } catch (error) {
        console.error('Error in poses update callback:', error);
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stop();
    this.posesCallbacks = [];
    this.lastValidPosesData = [];
    if (this.poseLandmarker) {
      this.poseLandmarker.close();
      this.poseLandmarker = null;
    }
  }
}
